import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { DEFAULTS, simulate, annualSnaps, buildRetGrid, buildPR } from './lib/finance.js';
import { C, fmt, fmtDate, DISCLAIMER } from './lib/format.js';
import {
  unlock, encryptJSON, decryptJSON, vaultFingerprint,
  rememberKey, forgetKey, loadRememberedKey,
} from './lib/crypto.js';
import {
  getSettings, putSettings, listSnapshots, createSnapshot, deleteSnapshot,
  clearCache, OfflineError,
} from './lib/api.js';
import { makeSnapshot, buildExport, parseImport } from './lib/snapshots.js';

import { useWidth, Btn, Notice } from './ui.jsx';
import Lock from './Lock.jsx';
import Overview from './tabs/Overview.jsx';
import Forecast from './tabs/Forecast.jsx';
import RetireGrid from './tabs/RetireGrid.jsx';
import Tracker from './tabs/Tracker.jsx';
import Assets from './tabs/Assets.jsx';
import PostRetire from './tabs/PostRetire.jsx';
import History from './tabs/History.jsx';
import Values from './tabs/Values.jsx';

const TABS = [
  { id: 'overview', l: 'Overview' },
  { id: 'forecast', l: 'Forecast' },
  { id: 'retirement', l: 'Retire Grid' },
  { id: 'tracker', l: 'Tracker' },
  { id: 'assets', l: 'Assets' },
  { id: 'income', l: 'Post-Retire' },
  { id: 'history', l: '📌 History' },
  { id: 'settings', l: '⚙ Values' },
];

export default function App() {
  // ── Vault / auth ──
  const [vault, setVault] = useState(null);            // { vaultId, key }
  const [unlocking, setUnlocking] = useState(false);
  const [lockError, setLockError] = useState('');
  const [pendingNew, setPendingNew] = useState(null);  // vault awaiting "create new" confirmation
  const [booting, setBooting] = useState(true);

  // ── Data ──
  const [settings, setSettings] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [offline, setOffline] = useState(false);
  const [sync, setSync] = useState('idle');            // idle | saving | saved | error
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // ── View state ──
  const [tab, setTab] = useState('overview');
  const [income, setIncome] = useState(36000);
  const [deathAge, setDA] = useState(90);
  const [viewing, setViewing] = useState(null);        // read-only past snapshot

  const dirtyRef = useRef(false);

  const flash = useCallback((msg, tone = 'good') => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 4200);
  }, []);

  // ── Boot: restore a remembered key if the user opted in ──
  useEffect(() => {
    (async () => {
      const remembered = await loadRememberedKey();
      if (remembered) {
        try {
          await loadVault(remembered);
          setBooting(false);
          return;
        } catch {
          forgetKey(); // stale or unreadable — fall through to the lock screen
        }
      }
      setBooting(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load everything for an unlocked vault ──
  const loadVault = useCallback(async ({ vaultId, key }) => {
    const res = await getSettings(vaultId);
    if (res.isNew) {
      const err = new Error('NEW_VAULT');
      err.code = 'NEW_VAULT';
      throw err;
    }
    const loaded = await decryptJSON(key, res.payload);
    const snapRes = await listSnapshots(vaultId);
    const decrypted = [];
    for (const s of snapRes.snapshots) {
      try {
        const body = await decryptJSON(key, { iv: s.iv, ct: s.ct });
        decrypted.push({ id: s.id, createdAt: s.createdAt, label: body.label, settings: body.settings });
      } catch {
        // A snapshot we cannot read must not take the whole app down.
        decrypted.push({
          id: s.id, createdAt: s.createdAt, label: '⚠ Unreadable snapshot',
          settings: { ...DEFAULTS }, unreadable: true,
        });
      }
    }
    dirtyRef.current = false;
    setSettings({ ...DEFAULTS, ...loaded });
    setSnapshots(decrypted);
    setOffline(res.fromCache || snapRes.fromCache);
    setVault({ vaultId, key });
  }, []);

  // ── Unlock ──
  const handleUnlock = useCallback(async (passphrase, remember) => {
    setUnlocking(true);
    setLockError('');
    try {
      const v = await unlock(passphrase);
      try {
        await loadVault(v);
        if (remember) await rememberKey(v.vaultId, v.key);
      } catch (e) {
        if (e.code === 'NEW_VAULT') { setPendingNew({ ...v, remember }); return; }
        throw e;
      }
    } catch (e) {
      setLockError(
        e instanceof OfflineError
          ? 'You appear to be offline, and this device has no cached copy yet. Connect to the internet and try again.'
          : e.message || 'Something went wrong unlocking.'
      );
    } finally {
      setUnlocking(false);
    }
  }, [loadVault]);

  const confirmNewVault = useCallback(async () => {
    const { vaultId, key, remember } = pendingNew;
    setBusy(true);
    try {
      const fresh = { ...DEFAULTS };
      await putSettings(vaultId, await encryptJSON(key, fresh));
      if (remember) await rememberKey(vaultId, key);
      dirtyRef.current = false;
      setSettings(fresh);
      setSnapshots([]);
      setVault({ vaultId, key });
      setPendingNew(null);
      flash('New vault created. Your data is encrypted with this passphrase.');
    } catch (e) {
      setLockError(e.message || 'Could not create the vault.');
      setPendingNew(null);
    } finally {
      setBusy(false);
    }
  }, [pendingNew, flash]);

  const lockNow = useCallback(() => {
    if (!window.confirm('Lock the app and forget the passphrase on this device?')) return;
    forgetKey();
    if (vault) clearCache(vault.vaultId);
    setVault(null); setSettings(null); setSnapshots([]); setViewing(null);
    setTab('overview'); setLockError('');
  }, [vault]);

  // ── Autosave working settings (debounced; never while replaying history) ──
  useEffect(() => {
    if (!vault || !settings || !dirtyRef.current || viewing) return;
    setSync('saving');
    const t = setTimeout(async () => {
      try {
        await putSettings(vault.vaultId, await encryptJSON(vault.key, settings));
        dirtyRef.current = false;
        setSync('saved');
        setOffline(false);
      } catch {
        setSync('error');
        setOffline(true);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [settings, vault, viewing]);

  const upd = useCallback((k, v) => {
    dirtyRef.current = true;
    setSettings(p => ({ ...p, [k]: v }));
  }, []);

  const onReset = useCallback(() => {
    if (!window.confirm('Reset all values to the original May 2026 defaults? Your saved checkups are not affected.')) return;
    dirtyRef.current = true;
    setSettings({ ...DEFAULTS });
  }, []);

  // ── Snapshots ──
  const onSaveCheckup = useCallback(async (label) => {
    if (!vault || !settings) return;
    setBusy(true);
    try {
      const snap = makeSnapshot(label, settings);
      const payload = await encryptJSON(vault.key, { label: snap.label, settings: snap.settings });
      await createSnapshot(vault.vaultId, { id: snap.id, createdAt: snap.createdAt, ...payload });
      setSnapshots(prev => [snap, ...prev]);
      flash(`Checkup saved: ${snap.label}`);
    } catch (e) {
      flash(e.message || 'Could not save the checkup.', 'danger');
    } finally {
      setBusy(false);
    }
  }, [vault, settings, flash]);

  const onDeleteSnapshot = useCallback(async (snap) => {
    if (!window.confirm(`Delete the checkup "${snap.label}" from ${fmtDate(snap.createdAt)}?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteSnapshot(vault.vaultId, snap.id);
      setSnapshots(prev => prev.filter(s => s.id !== snap.id));
      if (viewing?.id === snap.id) setViewing(null);
      flash('Checkup deleted.');
    } catch (e) {
      flash(e.message || 'Could not delete the checkup.', 'danger');
    } finally {
      setBusy(false);
    }
  }, [vault, viewing, flash]);

  const onOpenSnapshot = useCallback((snap) => {
    setViewing(snap);
    setTab('overview');
    window.scrollTo(0, 0);
  }, []);

  // ── Backup ──
  const onExport = useCallback(() => {
    try {
      const data = buildExport(settings, snapshots.map(({ id, createdAt, label, settings: st }) => ({ id, createdAt, label, settings: st })));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `retirement-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flash('Backup file created. Save it somewhere safe.');
    } catch (e) {
      flash('Could not create the backup file: ' + e.message, 'danger');
    }
  }, [settings, snapshots, flash]);

  const onImport = useCallback(async (file) => {
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = parseImport(text);
      const existing = new Set(snapshots.map(s => s.id));
      const incoming = parsed.snapshots.filter(s => !existing.has(s.id));

      const msg =
        `Restore from this backup?\n\n` +
        `• Current settings will be REPLACED with the backup's\n` +
        `• ${incoming.length} new checkup${incoming.length === 1 ? '' : 's'} will be added\n` +
        `• Your ${existing.size} existing checkup${existing.size === 1 ? '' : 's'} will be kept\n\n` +
        (parsed.exportedAt ? `Backup was made ${fmtDate(parsed.exportedAt)}.` : '');
      if (!window.confirm(msg)) return;

      // Settings first, so a failure part-way through still leaves the
      // working values restored.
      await putSettings(vault.vaultId, await encryptJSON(vault.key, parsed.settings));
      dirtyRef.current = false;
      setSettings(parsed.settings);

      let added = 0;
      for (const s of incoming) {
        try {
          const payload = await encryptJSON(vault.key, { label: s.label, settings: s.settings });
          await createSnapshot(vault.vaultId, { id: s.id, createdAt: s.createdAt, ...payload });
          added++;
        } catch { /* skip duplicates / individual failures */ }
      }
      setSnapshots(prev => [...incoming, ...prev].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      flash(`Restored. ${added} checkup${added === 1 ? '' : 's'} added.`);
    } catch (e) {
      flash(e.message || 'Could not read that backup file.', 'danger');
    } finally {
      setBusy(false);
    }
  }, [vault, snapshots, flash]);

  // ── Layout ──
  const W = useWidth();
  const isNarrow = W < 700, isMid = W < 960;
  const L = useMemo(() => ({
    isNarrow, isMid,
    g4: isNarrow ? '1fr 1fr' : isMid ? '1fr 1fr' : 'repeat(4,1fr)',
    g2: isNarrow ? '1fr' : '1fr 1fr',
    g6: isNarrow ? 'repeat(2,1fr)' : isMid ? 'repeat(3,1fr)' : 'repeat(auto-fit,minmax(110px,1fr))',
    settingsG: isMid ? '1fr' : '1fr 1fr',
    ch: base => (isNarrow ? Math.round(base * .72) : isMid ? Math.round(base * .88) : base),
    padH: isNarrow ? 14 : 22,
    padV: isNarrow ? 16 : 24,
  }), [isNarrow, isMid]);

  // ── Model (uses the snapshot's settings while replaying history) ──
  const s = viewing ? viewing.settings : settings;

  const monthly = useMemo(() => (s ? simulate(s) : []), [s]);
  const annual = useMemo(() => annualSnaps(monthly), [monthly]);
  const retGrid = useMemo(() => (s ? buildRetGrid(annual, s) : {}), [annual, s]);
  const retYr = retGrid[income]?.[deathAge] ?? 2049;
  const retSnap = annual.find(d => d.year === retYr);
  const postRet = useMemo(
    () => (retSnap && s ? buildPR(retSnap, retYr, income, deathAge, s) : []),
    [retSnap, retYr, income, deathAge, s]
  );
  const tracker = useMemo(() => monthly.slice(0, 120), [monthly]);

  const d = useMemo(() => {
    if (!s) return {};
    const infl = (s.inflationPct || 2.5) / 100;
    const sp0 = s.statePension2026 || 11502;
    const jSPY = s.jamesStatePensionYear || 2039, oSPY = s.joanneStatePensionYear || 2041;
    return {
      jSPY, oSPY,
      jSPN: sp0 * Math.pow(1 + infl, jSPY - 2026),
      oSPN: sp0 * Math.pow(1 + infl, oSPY - 2026),
    };
  }, [s]);

  const donut = useMemo(() => (s ? [
    { name: 'James Pension', v: Math.round(s.jamesPension), c: C.blue },
    { name: 'Joanne Pension', v: Math.round(s.joannePension), c: C.pink },
    { name: 'James ISA', v: Math.round(s.jamesISA), c: '#38BDF8' },
    { name: 'Joanne ISA', v: Math.round(s.joanneISA), c: '#FDA4AF' },
    { name: 'James PBs', v: Math.round(s.jamesPB), c: C.gold },
    { name: 'Joanne PBs', v: Math.round(s.joannePB), c: '#E5CA6C' },
    { name: 'Company (net)', v: Math.round(s.company * 0.9), c: C.teal },
  ] : []), [s]);
  const totalNow = donut.reduce((acc, x) => acc + x.v, 0);

  // ── Render gates ──
  if (booting) {
    return <Splash>Loading…</Splash>;
  }

  if (!vault || !settings) {
    return (
      <Lock
        onUnlock={handleUnlock}
        pending={unlocking || busy}
        error={lockError}
        newVault={pendingNew}
        onConfirmNew={confirmNewVault}
        onCancelNew={() => { setPendingNew(null); setLockError(''); }}
      />
    );
  }

  const { padH, padV } = L;
  const tbS = id => ({
    background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
    fontFamily: "'DM Sans',system-ui", fontSize: isNarrow ? 12 : 14,
    fontWeight: tab === id ? 600 : 400,
    color: tab === id ? C.gold : C.dim,
    borderBottom: `3px solid ${tab === id ? C.gold : 'transparent'}`,
    padding: isNarrow ? '12px 12px' : '14px 18px', minHeight: 48,
    display: 'flex', alignItems: 'center', transition: 'color .15s',
  });

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif", background: C.bg, minHeight: '100vh', color: C.text }}>

      {/* ── Replaying a past snapshot ── */}
      {viewing && (
        <div style={{
          background: C.purp + '22', borderBottom: `1px solid ${C.purp}66`,
          padding: `12px ${padH}px`, display: 'flex', gap: 12,
          alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
          // Deliberately NOT sticky: two sticky bars both pinned to top:0 would
          // stack on top of each other and hide the tabs.
        }}>
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
            <strong style={{ color: C.purp }}>Viewing a saved checkup —</strong> {viewing.label} ·{' '}
            {fmtDate(viewing.createdAt)}. Read-only; every tab shows the forecast exactly as it was.
          </div>
          <Btn onClick={() => setViewing(null)} style={{ minHeight: 44, padding: '10px 16px', fontSize: 14 }}>
            ← Back to current
          </Btn>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{
        background: `linear-gradient(135deg,${C.surf},${C.card})`,
        borderBottom: `1px solid ${C.bdr}`, padding: `16px ${padH}px`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: isNarrow ? 20 : 26, fontWeight: 700, color: C.bright, letterSpacing: '-.5px' }}>
              James &amp; Joanne
            </h1>
            <p style={{ fontSize: 10, color: C.dim, letterSpacing: '2px', textTransform: 'uppercase', marginTop: 2 }}>
              {s.asOfDate || 'Current'}
              {!viewing && sync === 'saving' && <span style={{ color: C.steel }}> · saving…</span>}
              {!viewing && sync === 'saved' && <span style={{ color: C.teal }}> · saved</span>}
              {!viewing && sync === 'error' && <span style={{ color: C.amb }}> · not synced</span>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: isNarrow ? 14 : 24, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>Investable</div>
              <div style={{ fontSize: isNarrow ? 17 : 21, fontWeight: 700, color: C.gold, fontFamily: "'DM Mono',monospace" }}>
                {fmt(totalNow)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>Incl. Property</div>
              <div style={{ fontSize: isNarrow ? 17 : 21, fontWeight: 700, color: C.bright, fontFamily: "'DM Mono',monospace" }}>
                {fmt(totalNow + (s.houseValue || 0) - (s.mortgageRemaining || 0))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      {/* Sticks below the status bar, not under it: on a translucent status bar
          the inset is the notch height, and on an opaque one it resolves to 0. */}
      <div className="scroll-x" style={{
        background: C.surf, borderBottom: `1px solid ${C.bdr}`, display: 'flex', gap: 0,
        padding: `0 ${padH - 8}px`, position: 'sticky', top: 'env(safe-area-inset-top, 0px)', zIndex: 30,
      }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={tbS(t.id)}>{t.l}</button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: `${padV}px ${padH}px`, maxWidth: 1400, margin: '0 auto' }}>

        {offline && (
          <div style={{ marginBottom: 16 }}>
            <Notice tone="warn">
              <strong style={{ color: C.bright }}>Offline.</strong> You're seeing the last copy saved on this device.
              Changes you make now won't sync until you're back online.
            </Notice>
          </div>
        )}

        {tab === 'overview' && <Overview s={s} retGrid={retGrid} setIncome={setIncome} setDA={setDA} setTab={setTab} L={L} d={d} />}
        {tab === 'forecast' && <Forecast annual={annual} L={L} />}
        {tab === 'retirement' && <RetireGrid retGrid={retGrid} income={income} setIncome={setIncome} deathAge={deathAge} setDA={setDA} retYr={retYr} retSnap={retSnap} L={L} />}
        {tab === 'tracker' && <Tracker s={s} tracker={tracker} />}
        {tab === 'assets' && <Assets s={s} annual={annual} donut={donut} totalNow={totalNow} L={L} />}
        {tab === 'income' && <PostRetire s={s} income={income} setIncome={setIncome} deathAge={deathAge} setDA={setDA} retYr={retYr} retSnap={retSnap} postRet={postRet} L={L} d={d} />}

        {tab === 'history' && (
          viewing ? (
            <Notice tone="warn">
              History isn't available while you're viewing a past checkup. Tap
              <strong style={{ color: C.bright }}> ← Back to current</strong> at the top to return.
            </Notice>
          ) : (
            <History
              snapshots={snapshots} settings={settings}
              onSaveCheckup={onSaveCheckup} onOpenSnapshot={onOpenSnapshot} onDeleteSnapshot={onDeleteSnapshot}
              busy={busy} L={L}
            />
          )
        )}

        {tab === 'settings' && (
          viewing ? (
            <Notice tone="warn">
              These are the values as they stood on {fmtDate(viewing.createdAt)}, frozen and read-only. Tap
              <strong style={{ color: C.bright }}> ← Back to current</strong> at the top to edit today's figures.
            </Notice>
          ) : (
            <Values
              s={settings} upd={upd} onReset={onReset}
              onExport={onExport} onImport={onImport}
              fingerprint={vaultFingerprint(vault.vaultId)} onLock={lockNow}
              L={L}
            />
          )
        )}

        <p style={{ fontSize: 11, color: C.dim, marginTop: 28, lineHeight: 1.6, textAlign: 'center', paddingBottom: 20 }}>
          {DISCLAIMER}
        </p>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', left: 16, right: 16, bottom: 24, zIndex: 60,
          display: 'flex', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{
            background: toast.tone === 'danger' ? C.red : C.teal,
            color: '#04121A', fontWeight: 600, fontSize: 14, borderRadius: 12,
            padding: '14px 20px', maxWidth: 520, textAlign: 'center',
            boxShadow: '0 8px 28px rgba(0,0,0,.45)', lineHeight: 1.5,
          }}>{toast.msg}</div>
        </div>
      )}
    </div>
  );
}

function Splash({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh',
      background: C.bg, color: C.dim, fontFamily: 'system-ui', fontSize: 16, letterSpacing: 1,
    }}>{children}</div>
  );
}
